import { jsPDF } from "jspdf"
import { autoTable } from "jspdf-autotable"
import * as xlsx from "xlsx"

import { formatDateCell } from "./utils"
import { downloadScreenShotFile } from "Utils/screenshotPage"

export async function exportExcelFromCsv(gridApi, fileName) {
  if (!gridApi) return

  // 1) Get CSV data
  const csvData = gridApi.getDataAsCsv({ allColumns: true })

  // 2) Parse CSV data
  const csvSheet = xlsx.read(csvData, { type: "string" }).Sheets.Sheet1
  const parsedRows = xlsx.utils.sheet_to_json(csvSheet, { header: 1, raw: false })

  if (parsedRows.length < 2) return // No data to export

  // Regex helpers
  const numberRegex = /^-?\d+(\.\d+)?$/
  // Accepts YYYY-MM-DD, YYYY/MM/DD, DD/MM/YYYY, DD-MM-YYYY, YY-MM-DD, DD-MM-YY, etc.
  const dateRegex = /^(\d{2,4}[-/]\d{1,2}[-/]\d{1,2}|\d{1,2}[-/]\d{1,2}[-/]\d{2,4})$/

  // 3) Convert data types
  const convertedRows = parsedRows.map((row, rowIndex) => {
    if (rowIndex === 0) return row // header
    return row.map(cell => {
      if (typeof cell !== "string") return cell

      if (numberRegex.test(cell)) {
        return Number(cell)
      }
      if (dateRegex.test(cell)) {
        // YYYY-MM-DD, YYYY/MM/DD, YY-MM-DD, YY/MM/DD
        const isoMatch = cell.match(/^(\d{2,4})[-/](\d{1,2})[-/](\d{1,2})$/)
        // DD-MM-YYYY, DD/MM/YYYY, DD-MM-YY, DD/MM/YY
        const dmyMatch = cell.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{2,4})$/)

        let year, month, day
        if (dmyMatch) {
          ;[, day, month, year] = dmyMatch
        } else if (isoMatch) {
          ;[, year, month, day] = isoMatch
        }
        if (year && month && day) {
          if (year.length === 2) {
            year = Number(year) < 70 ? "20" + year : "19" + year
          }
          const date = new Date(`${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`)
          return !isNaN(date.getTime()) ? date : cell
        }
      }
      return cell
    })
  })

  // 4) Create worksheet and workbook
  const ws = xlsx.utils.aoa_to_sheet(convertedRows)
  const wb = xlsx.utils.book_new()
  xlsx.utils.book_append_sheet(wb, ws, "Data")

  // 5) Download xlsx file
  xlsx.writeFile(wb, `${fileName}.xlsx`)
}

/**
 * It exports the table as a PNG file.
 * @param widgetTitle - The title of the widget.
 * @param tableId - The id of the table to export.
 * @param exportMenu - the export menu element
 */
export const exportPNG = (widgetTitle, tableId, exportMenu) => {
  const exportTitle = widgetTitle.replace(/ /g, "_")
  // hide export menu
  exportMenu.style.display = "none"
  downloadScreenShotFile(tableId, exportTitle)
  // stop hiding export menu
  setTimeout(() => exportMenu.removeAttribute("style"), 10)
}

/**
 * This function exports the current grid data as a CSV file
 * @param gridRef - The reference to the grid.
 * @param title - The name of the file to be exported.
 */
export const exportCSV = (gridRef, title) => {
  if (gridRef?.api) {
    gridRef?.api?.exportDataAsCsv({
      fileName: title,
    })
  }
}

export const exportXLSX = (gridRef, title) => {
  if (gridRef?.api) {
    exportExcelFromCsv(gridRef?.api, title)
  }
}

/**
 * It exports the data in the table to a file of the type specified.
 * @param columns - an array of objects that contain the following properties:
 * @param data - The data to be exported.
 * @param fileType - The type of file to export. Either "xlsx" or "pdf".
 * @param fileName - The name of the file to be exported.
 * @param [pdfStyle] - {}
 */
export const exportTable = (columns, data, fileType, fileName = "fileName", pdfStyle = {}) => {
  if (fileType === "xlsx") {
    // XLSX example
    const header = columns.map(c => c.headerName)
    const excelData = data.map(row => {
      const obj = {}
      columns.forEach(({ field, type = "", headerName }, index) => {
        let value = row[field]
        if (type === "date") value = formatDateCell(value)
        obj[headerName] = value
      })
      return obj
    })

    let wb = xlsx.utils.book_new()
    let ws1 = xlsx.utils.json_to_sheet(excelData, {
      header,
    })
    xlsx.utils.book_append_sheet(wb, ws1, "export")
    xlsx.writeFile(wb, `${fileName}.xlsx`)
  }
  //PDF example
  if (fileType === "pdf") {
    const headerNames = columns.map(column => column.headerName)
    const values = data.map(row =>
      Object.values(row).map((value, index) => {
        const { type = "" } = columns[index]
        // if (type === "date")
        //   return formatDateCell(value)
        // else
        return value
      })
    )
    const doc = new jsPDF({ orientation: "landscape" })
    autoTable(doc, {
      head: [headerNames],
      headStyles: pdfStyle,
      body: values,
      margin: { top: 20 },
      styles: {
        minCellHeight: 9,
        halign: "left",
        valign: "center",
        fontSize: 11,
      },
    })
    const numberOfPages = doc.internal.getNumberOfPages()
    for (let i = 1; i <= numberOfPages; i++) {
      doc.setPage(i)
      doc.text(140, 205, `Page ${i} of ${numberOfPages}`)
    }
    doc.save(`${fileName}.pdf`)
  }
}
